import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Check, Zap, Shield, Users, TrendingUp, Phone, Mail, CreditCard, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

export default function Pricing() {
  const creditPackages = [
    { 
      id: 'starter', 
      name: 'Starter', 
      credits: 10, 
      price: 49, 
      perLead: 4.90,
      description: 'Perfect for trying out the platform',
      highlight: 'starter'
    },
    { 
      id: 'business', 
      name: 'Business', 
      credits: 25, 
      price: 99, 
      perLead: 3.96,
      description: 'Great value for growing businesses',
      highlight: 'best-value'
    },
    { 
      id: 'enterprise', 
      name: 'Enterprise', 
      credits: 50, 
      price: 179, 
      perLead: 3.58,
      description: 'For busy tradies who want consistent leads',
      highlight: 'popular'
    },
    { 
      id: 'premium', 
      name: 'Premium', 
      credits: 100, 
      price: 299, 
      perLead: 2.99,
      description: 'Maximum savings for high-volume needs',
      highlight: 'premium'
    },
  ];

  const features = [
    { icon: Users, title: 'Qualified Leads', description: 'Real customers actively looking for your services in your area' },
    { icon: Phone, title: 'Direct Contact', description: 'Get phone numbers and emails - contact leads directly, no middleman' },
    { icon: Shield, title: 'No Lock-in Contracts', description: 'Buy credits when you need them, no monthly fees or commitments' },
    { icon: TrendingUp, title: 'Grow Your Business', description: 'Turn leads into paying customers and build your reputation' },
  ];

  const howItWorks = [
    { step: 1, title: 'Get Listed', description: 'Create your free listing on Local Tradies Online' },
    { step: 2, title: 'Buy Credits', description: 'Purchase a credit pack that suits your needs' },
    { step: 3, title: 'Receive Leads', description: 'When customers request quotes, you get notified' },
    { step: 4, title: 'Unlock & Contact', description: 'Use credits to unlock contact details and win the job' },
  ];

  return (
    <>
      <Helmet>
        <title>Pricing - Local Tradies Online | Lead Credit Packages for Tradies</title>
        <meta name="description" content="Affordable lead generation for Australian tradies. Buy credits to unlock customer contact details. No monthly fees, no contracts. From $2.99 per lead." />
      </Helmet>
      
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        
        {/* Hero */}
        <section className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-16">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <div className="inline-flex items-center bg-orange-500/20 text-orange-300 px-4 py-2 rounded-full text-sm mb-6">
              <Zap className="w-4 h-4 mr-2" />
              Simple, Transparent Pricing
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Pay Per Lead, Not Per Month
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              No subscriptions. No contracts. Just buy credits and use them to unlock qualified customer leads when you need them.
            </p>
          </div>
        </section>

        {/* Credit Packages */}
        <section className="py-16 px-4 -mt-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {creditPackages.map((pkg) => (
                <Card 
                  key={pkg.id}
                  className={`relative overflow-hidden transition-all hover:shadow-xl ${
                    pkg.highlight === 'popular' 
                      ? 'border-orange-500 ring-2 ring-orange-200 bg-gradient-to-b from-orange-50 to-white' 
                      : pkg.highlight === 'best-value'
                      ? 'border-blue-400 ring-2 ring-blue-100'
                      : pkg.highlight === 'starter'
                      ? 'border-emerald-400 ring-2 ring-emerald-100'
                      : pkg.highlight === 'premium'
                      ? 'border-purple-400 ring-2 ring-purple-100'
                      : 'border-slate-200 bg-white'
                  }`}
                >
                  {pkg.highlight === 'popular' && (
                    <div className="absolute top-0 right-0 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                      MOST POPULAR
                    </div>
                  )}
                  {pkg.highlight === 'best-value' && (
                    <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                      BEST VALUE
                    </div>
                  )}
                  
                  <CardHeader className="pb-4 pt-8">
                    <CardTitle className="text-xl">{pkg.name}</CardTitle>
                    <p className="text-sm text-slate-500">{pkg.description}</p>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="mb-6">
                      <div className="flex items-baseline">
                        <span className="text-4xl font-bold text-slate-900">${pkg.price}</span>
                        <span className="text-slate-500 ml-2">AUD</span>
                      </div>
                      <p className="text-sm text-green-600 font-medium mt-1">
                        ${pkg.perLead.toFixed(2)} per lead
                      </p>
                    </div>
                    
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-center text-sm">
                        <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        <span className="font-medium">{pkg.credits} lead credits</span>
                      </li>
                      <li className="flex items-center text-sm">
                        <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        <span>Full contact details</span>
                      </li>
                      <li className="flex items-center text-sm">
                        <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        <span>Credits never expire</span>
                      </li>
                      <li className="flex items-center text-sm">
                        <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        <span>Instant delivery</span>
                      </li>
                    </ul>
                    
                    <Link to="/buy-credits">
                      <Button 
                        className={`w-full ${
                          pkg.highlight === 'popular' 
                            ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                            : pkg.highlight === 'best-value'
                            ? 'bg-blue-500 hover:bg-blue-600 text-white'
                            : pkg.highlight === 'starter'
                            ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                            : pkg.highlight === 'premium'
                            ? 'bg-purple-500 hover:bg-purple-600 text-white'
                            : 'bg-slate-800 hover:bg-slate-900 text-white'
                        }`}
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Get Started
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                How It Works
              </h2>
              <p className="text-lg text-slate-600">
                Start getting leads in four simple steps
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-8">
              {howItWorks.map((item, index) => (
                <div key={item.step} className="text-center relative">
                  {index < howItWorks.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-slate-200" />
                  )}
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                    <span className="text-2xl font-bold text-orange-600">{item.step}</span>
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 px-4 bg-slate-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Why Tradies Choose Us
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {features.map((feature) => (
                <Card key={feature.title} className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <feature.icon className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1">{feature.title}</h3>
                        <p className="text-slate-600">{feature.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Frequently Asked Questions
              </h2>
            </div>
            
            <div className="space-y-4">
              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-2">What exactly is a lead credit?</h3>
                  <p className="text-slate-600">One credit unlocks one customer's full contact details (phone and email). When a customer in your area requests a quote for your trade, you can use a credit to access their information and reach out directly.</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-2">Do I need to pay monthly?</h3>
                  <p className="text-slate-600">No! There are no monthly fees or subscriptions. You simply buy credits when you need them. Your credits never expire, so you can use them at your own pace.</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-2">How do I know the leads are real?</h3>
                  <p className="text-slate-600">All leads come from real customers who have filled out our quote request form. They've provided their contact details because they're actively looking for a tradie in their area.</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-2">Can I get a refund on unused credits?</h3>
                  <p className="text-slate-600">Credits are non-refundable once purchased. However, they never expire, so you can always use them later. We recommend starting with a smaller pack to test the platform.</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-2">Is my listing free?</h3>
                  <p className="text-slate-600">Yes! Creating a listing on Local Tradies Online is completely free. You only pay when you want to unlock a lead's contact details.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4 bg-gradient-to-r from-orange-500 to-orange-600">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Ready to Grow Your Business?
            </h2>
            <p className="text-xl text-orange-100 mb-8">
              Join thousands of Australian tradies getting quality leads every day.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/join">
                <Button size="lg" className="bg-white text-orange-600 hover:bg-orange-50 font-bold">
                  Create Free Listing
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/buy-credits">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-bold">
                  Buy Credits Now
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
